import { z } from "zod";

import {
  type TemplatedText,
  TemplatedTextSchema,
  templateToString,
} from "./text";

const baseFieldSchema = z.object({
  name: z.string(),
  description: TemplatedTextSchema.optional().nullable(),
  userChoice: z.boolean().optional(),
});

type BaseField = z.infer<typeof baseFieldSchema>;

export type ListModel = BaseField & {
  type: "array";
  model: Model; // Recurse
};

export type ObjectModel = BaseField & {
  type: "object";
  fields: Model[]; // Recurse
};

export enum FormulaCoercion {
  /**
   * Truthy, Falsy, and Null values respectively are coerced to "passed", "failed", "not_applicable"
   */
  PASS_FAIL_NA = "pass_fail_not_applicable",

  /**
   * Truthy and falsy values are coerced to "true", "false"
   */
  BOOLEAN = "boolean",
}

export type FormulaModel = BaseField & {
  type: "formula";
  formula?: TemplatedText;
  coerce?: FormulaCoercion;
};

export type Model =
  // Primitives
  | (BaseField & { type: "string" })
  | (BaseField & { type: "int" })
  | (BaseField & { type: "float" })
  | (BaseField & { type: "boolean" })
  | (BaseField & { type: "enum"; types: string[] })

  // Special
  | FormulaModel
  | (BaseField & { type: "const-string"; value: string })

  // Collections
  | ObjectModel
  | ListModel;

export type ModelFieldType = Model["type"];

export const ModelSchema: z.ZodType<Model> = z.lazy(() =>
  z.discriminatedUnion("type", [
    // Primitives
    baseFieldSchema.extend({ type: z.literal("string") }),
    baseFieldSchema.extend({ type: z.literal("int") }),
    baseFieldSchema.extend({ type: z.literal("float") }),
    baseFieldSchema.extend({ type: z.literal("boolean") }),
    baseFieldSchema.extend({
      type: z.literal("enum"),
      types: z.array(z.string()),
    }),
    baseFieldSchema.extend({
      type: z.literal("const-string"),
      value: z.string(),
    }),

    // Formula
    baseFieldSchema.extend({
      type: z.literal("formula"),
      formula: TemplatedTextSchema.optional(),
      coerce: z.nativeEnum(FormulaCoercion).optional(),
    }),

    // Collections
    baseFieldSchema.extend({
      type: z.literal("object"),
      // fields: z.record(z.string(), ModelSchema),
      fields: z.array(ModelSchema),
    }),
    baseFieldSchema.extend({
      type: z.literal("array"),
      model: ModelSchema,
    }),
  ]),
);

// biome-ignore lint/suspicious/noExplicitAny: This can be anything...
export type ModelResult = any;

export const ModelResultSchema = z.object({
  data: z.any().nullable().optional(),
  /**
   * .citations will have the same data structure as .data, but at every leaf will be a uuid
   * that will point to a row in the table
   **/
  citations: z.any().nullable().optional(),
});

export const compileModelZodSchema = (model: Model): z.ZodTypeAny => {
  const buildSchema = (model: Model): z.ZodTypeAny => {
    if (model.type === "object") {
      return z
        .object(
          model.fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
            // Fields with empty string values are not included
            if (!field.name) return acc;

            acc[field.name] = buildSchema(field);
            return acc;
          }, {}),
        )
        .describe(templateToString(model.description ?? ""));
    }

    if (model.type === "array") {
      return z.array(buildSchema(model.model));
    }

    let schema: z.ZodTypeAny;
    switch (model.type) {
      case "string":
        // NOTE(yogi): Coerce fields to strings.. Some models like 4o might return numbers
        // But don't use `.coerce` because that makes values undefined
        // schema = z.preprocess((val) => String(val), z.string());
        // But.... this breaks o3 models for some reason, so just using regular z.string()
        schema = z.string();
        break;
      case "int":
        schema = z.number().int();
        break;
      case "float":
        schema = z.number();
        break;
      case "boolean":
        schema = z.boolean();
        break;
      case "enum":
        schema = z.enum(model.types as [string, ...string[]]);
        break;
      case "formula":
        // Shouldn't end up here.
        schema = z.any();
        break;
      case "const-string":
        // The value will always be model.value
        schema = z.enum([model.value]);
        break;
    }

    schema = schema.describe(templateToString(model.description ?? ""));

    return schema;
  };

  return buildSchema(model);
};

export const compileModelResultZodSchema = (model: Model): z.ZodTypeAny => {
  const buildSchema = (model: Model): z.ZodTypeAny => {
    if (model.type === "object") {
      return z
        .object(
          model.fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
            acc[field.name] = buildSchema(field);
            return acc;
          }, {}),
        )
        .describe(templateToString(model.description ?? ""));
    }

    if (model.type === "array") {
      return z.object({
        value: z.array(buildSchema(model.model)),
      });
    }

    let schema: z.ZodTypeAny;
    switch (model.type) {
      case "string":
        schema = z.object({
          sourceIds: z.array(z.string()),
          value: z.string(),
        });
        break;
      case "int":
        schema = z.object({
          sourceIds: z.array(z.string()),
          value: z.number().int(),
        });
        break;
      case "float":
        schema = z.object({
          sourceIds: z.array(z.string()),
          value: z.number(),
        });
        break;
      case "boolean":
        schema = z.object({
          sourceIds: z.array(z.string()),
          value: z.boolean(),
        });
        break;
      case "enum":
        schema = z.object({
          sourceIds: z.array(z.string()),
          value: z.enum(model.types as [string, ...string[]]),
        });
        break;
      case "formula":
        // Shouldn't end up here.
        schema = z.object({
          sourceIds: z.array(z.string()),
          value: z.any(),
        });
        break;
      case "const-string":
        // The value will always be model.value
        schema = z.object({
          sourceIds: z.array(z.string()),
          value: z.enum([model.value]),
        });
        break;
    }

    schema = schema.describe(templateToString(model.description ?? ""));

    return schema;
  };

  return buildSchema(model);
};

export const mapModel = (
  model: Model,
  apply: (m: Model, path: string[]) => void,
) => {
  const map = (m: Model, path: string[]) => {
    if (m.type === "object") {
      apply(m, path);
      for (const f of m.fields) {
        map(f, [...path, m.name]);
      }
    } else if (m.type === "array") {
      apply(m, path);
      map(m.model, [...path, m.name]);
    } else {
      apply(m, path);
    }
  };

  const result = structuredClone(model);
  map(result, []);
  return result;
};

export const getModelAtPath = (model: Model, path: string[]) => {
  let ret: Model | undefined = model;
  for (const p of path) {
    if (ret == null) break;

    if (ret.type === "object") {
      ret = ret.fields.find((f) => f.name === p);
    } else if (ret.type === "array") {
      ret = ret.model;
    }
  }

  return ret;
};

export const getChildFields = (model: Model): string[] => {
  if (model.type === "object") {
    return model.fields.map((field) => field.name);
  }

  if (model.type === "array") {
    return getChildFields(model.model);
  }

  return [];
};

export const getListModelPaths = (model: Model) => {
  const paths: string[][] = [];

  mapModel(model, (m, path) => {
    if (m.type === "array") {
      // slice 1 to remove the INTERNAL_RUN_MODEL_NAME
      paths.push([...path.slice(1), m.name]);
    }
  });

  return paths;
};

export const isModelEmpty = (model: Model): boolean => {
  if (model.type === "object") {
    return model.fields.length === 0;
  }

  if (model.type === "array") {
    return isModelEmpty(model.model);
  }

  if (model.type === "formula") {
    return !model.formula;
  }

  if (model.type === "const-string") {
    return model.value === "";
  }

  if (model.type === "enum") {
    return model.types.length === 0;
  }

  return false;
};
